import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { I18n } from 'aws-amplify';
import { fetchAccountLicenses, getSolarProDownloadUrl } from '../../components/util/callApi';
import { LANG_LABELS, PRODUCT } from '../../configures/val';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import Button from '../../components/common/Button';
import styles from './styles.module.css';

/**
 * Solar Pro をダウンロードする
 */
const download = async (langCode) => {
  try {
    const res = await getSolarProDownloadUrl(langCode);
    if (!res.ok) throw new Error('Failed to get solar pro installation url.');
    window.open(res.data.url, '_blank');
  } catch (err) {
    console.error(err);
  }
};

/**
 * Solar Pro インストーラー関連画面
 * @param {Object} props
 * @param {Function} props.updateHeader - ヘッダの更新メソッド
 * @param {Object[]} props.accounts - アカウント情報
 * @returns {JSX.Element}
 */
const SolarProInstallerRelatedInfo = ({ updateHeader, accounts }) => {
  const breadcrumbsListItems = [
    {
      type: 'link',
      text: I18n.get('Service list'),
      to: '/servicelist',
    },
    {
      type: 'link',
      text: 'Solar Pro',
      to: '/servicelist/solarpro',
    },
    {
      type: 'text',
      text: I18n.get('Installer Related Information'),
    },
  ];

  const updateHeaderRef = useRef(updateHeader);
  const [licenses, setLicenses] = useState([]);

  /**
   * Solar Pro のライセンスキーが発行されているかどうか
   */
  const hasSolarProLicense = useMemo(() => {
    return licenses.length > 0;
  }, [licenses]);

  const loadLicenses = useCallback(async () => {
    try {
      const newLicenses = [];
      for await (const { id } of accounts) {
        let fetchAccountLicensesRes = await fetchAccountLicenses({accountId: id, product: PRODUCT.SOLAR_PRO});
        if (!fetchAccountLicensesRes.ok) continue;
        const { licenseCount } = fetchAccountLicensesRes.data;
        fetchAccountLicensesRes = await fetchAccountLicenses({accountId: id, page: 1, limit: licenseCount, product: PRODUCT.SOLAR_PRO});
        if (!fetchAccountLicensesRes.ok) continue;
        newLicenses.push(...fetchAccountLicensesRes.data.licenses);
      }
      setLicenses(newLicenses);
    } catch (error) {
      console.error('Failed to fetch licenses data', error);
    }
  }, [accounts]);

  // ヘッダを更新する
  useEffect(() => {
    if (updateHeaderRef.current) updateHeaderRef.current();
  }, []);

  useEffect(() => {
    loadLicenses();
  }, [loadLicenses]);
  
  return (
    <div className={styles.container}>
      {hasSolarProLicense ? (
        <>
          <Breadcrumbs listItems={breadcrumbsListItems} />
          <div className={styles.buttonGroup}>
          {Object.entries(LANG_LABELS).map(([langCode, label]) => (
            <div key={langCode} className={styles.panel}>
              <p>{`Solar Pro ${I18n.get(`${label} Edition`)}`}</p>
              <Button
                text={`Solar Pro ${I18n.get('Download')}`}
                onClick={async () => await download(langCode)}
                size="exLarge"
              />
            </div>
          ))}
        </div>
        </>
      ) : (
        <p className={styles.error}>{I18n.get('You do not have permission to view this page.')}</p>
      )}
    </div>
  );
};

export default SolarProInstallerRelatedInfo;
